// Tiny promise-wrapped IndexedDB layer for the vault record.
//
// We keep one row in the `ladderbook_vault` object store under the key
// 'singleton'. The row carries:
//   - vaultId: stable UUID surfaced to the executor for per-user bucketing
//   - salt: PBKDF2 salt
//   - kalshi: { keyId, sealedPem (iv + ct) } | null
//   - polymarket: { address, pkSealed, l2Sealed } | null
//
// IndexedDB is the only browser store that survives reload while staying
// per-origin. LocalStorage is synchronous + small + plaintext-visible — not
// suitable for credentials, even encrypted ones (devtools-friendly leak).

import { SealedBlob } from './crypto.js';

const DB_NAME = 'ladderbook';
const DB_VERSION = 1;
const STORE = 'vault';
const KEY = 'singleton';

export interface KalshiVaultEntry {
  keyId: string;
  sealedPem: SealedBlob;
}

export interface PolymarketVaultEntry {
  // EOA address is public — kept plaintext so we can render it on the
  // profile page without unlocking the vault.
  address: `0x${string}`;
  // Sealed 32-byte hex EOA private key.
  pkSealed: SealedBlob;
  // Sealed JSON of the derived L2 creds: { apiKey, secret, passphrase }.
  l2Sealed: SealedBlob | null;
}

export interface VaultRecord {
  vaultId: string;
  salt: Uint8Array;
  kalshi: KalshiVaultEntry | null;
  polymarket: PolymarketVaultEntry | null;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb open failed'));
  });
  return dbPromise;
}

export async function readVault(): Promise<VaultRecord | null> {
  const db = await openDb();
  return new Promise<VaultRecord | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve((req.result as VaultRecord | undefined) ?? null);
    req.onerror = () => reject(req.error ?? new Error('idb read failed'));
  });
}

export async function writeVault(rec: VaultRecord): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(rec, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('idb write failed'));
    tx.onabort = () => reject(tx.error ?? new Error('idb write aborted'));
  });
}

export async function deleteVault(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('idb delete failed'));
  });
}
